import { useMemo } from "react";
import { useOnboardingProgress } from "../../common/hooks/useOnboardingProgress";
import { useSurvey } from "../../common/hooks/api/useSurvey";
import { NetworkRequest } from "../../common/models";
import {
  BUSINESS_SURVEY_NAME,
  businessSurvey,
  NEW_JERSEY_SENSITIVE_DATA_SELLING_DISABLED_SLUG,
} from "../surveys/businessSurvey";
import {
  CONSUMER_INCENTIVES_SURVEY_NAME,
  OFFERS_CONSUMER_INCENTIVES_SLUG,
} from "../surveys/consumerCollectionIncentivesSurvey";
import { EEA_UK_SURVEY_NAME, eeaVisibility } from "../surveys/eeaUkSurvey";
import { HR_SURVEY_NAME, hrVisibility } from "../surveys/hrSurvey";
import { questionsWithVisibility } from "../surveys/survey";
import { usePrimaryOrganization } from "./useOrganization";

const hasVisibleFlag = (flaggedSlugs: string[], visibility: Record<string, boolean>) =>
  flaggedSlugs.some((slug) => visibility[slug] === true);

/** Returns whether the Business Profile has a visible flagged question, or undefined while loading. */
export function useVisibleFlaggedBusinessProfileQuestions(): boolean | undefined {
  const [organization] = usePrimaryOrganization();
  const [onboardingProgress] = useOnboardingProgress(organization?.id);
  const {
    answers: businessAnswers,
    flaggedForReviewSlugs: flaggedBusinessSlugs,
    request: businessRequest,
  } = useSurvey(organization?.id, BUSINESS_SURVEY_NAME);
  const { flaggedForReviewSlugs: flaggedIncentivesSlugs, request: incentivesRequest } = useSurvey(
    organization?.id,
    CONSUMER_INCENTIVES_SURVEY_NAME,
  );
  const {
    answers: hrAnswers,
    flaggedForReviewSlugs: flaggedHrSlugs,
    request: hrRequest,
  } = useSurvey(organization?.id, HR_SURVEY_NAME);
  const {
    answers: eeaAnswers,
    flaggedForReviewSlugs: flaggedEeaSlugs,
    request: eeaRequest,
  } = useSurvey(organization?.id, EEA_UK_SURVEY_NAME);

  return useMemo(() => {
    if (
      !organization ||
      !onboardingProgress ||
      !NetworkRequest.areFinished(businessRequest, incentivesRequest, hrRequest, eeaRequest)
    ) {
      return undefined;
    }

    const businessQuestionVisibility = Object.fromEntries(
      questionsWithVisibility(businessSurvey(businessAnswers), businessAnswers).map((question) => [
        question.slug,
        question.visible,
      ]),
    );
    businessQuestionVisibility[NEW_JERSEY_SENSITIVE_DATA_SELLING_DISABLED_SLUG] =
      onboardingProgress.surveyOnboardingProgress.isNewJerseySensitiveDataSellingQuestionVisible;

    if (hasVisibleFlag(flaggedBusinessSlugs, businessQuestionVisibility)) {
      return true;
    }

    if (flaggedIncentivesSlugs.includes(OFFERS_CONSUMER_INCENTIVES_SLUG)) {
      return true;
    }

    if (hasVisibleFlag(flaggedHrSlugs, hrVisibility(hrAnswers, organization.featureGdpr))) {
      return true;
    }

    return organization.featureGdpr && hasVisibleFlag(flaggedEeaSlugs, eeaVisibility(eeaAnswers));
  }, [
    businessAnswers,
    businessRequest,
    eeaAnswers,
    eeaRequest,
    flaggedBusinessSlugs,
    flaggedEeaSlugs,
    flaggedHrSlugs,
    flaggedIncentivesSlugs,
    hrAnswers,
    hrRequest,
    incentivesRequest,
    onboardingProgress,
    organization,
  ]);
}
